// Main-thread client for the off-thread ML inference worker
// (src/workers/inference.worker.js).
//
// Why: model.executeAsync on the main thread blocks everything for the length
// of an inference (camera preview compositing on weak devices, recording draw
// loops, UI). Moving inference to a worker frees the main thread completely;
// per-frame accuracy is unchanged (same model, same 128x128 preprocessing).
//
// Design:
//   - getSharedInferenceWorker(app): page-lifetime singleton (mirrors the
//     global modelPromise pattern in context/utils). Returns null where
//     workers/createImageBitmap aren't available (e.g. old iOS) -- callers
//     keep using their existing main-thread inference in that case.
//   - client.detect(videoEl): grabs one frame via createImageBitmap
//     (transferred to the worker, zero-copy) and resolves with
//     { detections, rgbMeans }. Single-flight: the detect loops in the
//     components are sequential, so no queueing is needed.
//   - Self-disabling: after 3 consecutive failures the client marks itself
//     dead (isAlive() === false) and callers fall back to their main-thread
//     path permanently for the session. The main-thread model stays loaded,
//     so the fallback is always available.
//   - makeLowLightFrameShim(rgbMeans): minimal stand-in for the processed
//     tensor, satisfying exactly what GuidanceMain passes into
//     lowLightDetector.checkLowLight2Trimmed (squeeze -> sum([0,1]) ->
//     div(H*W) -> bufferSync().values) plus the dispose() the components call.

// Console diagnostics are silenced for production; set DEBUG = true to print
// [inferenceWorker] lines again during a device test.
const DEBUG = false;
const clog = (...args) => {
  if (!DEBUG) return;
  try {
    console.log("[inferenceWorker]", ...args);
  } catch (e) {}
};

// Must match the model input size used in the worker's processInput (and
// PartDetector.processInput's resizeBilinear target).
const MODEL_INPUT_SIZE = 128;

export const isInferenceWorkerSupported = () =>
  typeof Worker !== "undefined" && typeof createImageBitmap === "function";

export const makeLowLightFrameShim = (rgbMeans) => {
  const means =
    Array.isArray(rgbMeans) && rgbMeans.length >= 3
      ? rgbMeans
      : [255, 255, 255]; // bright default -> low-light check stays quiet
  const squeezed = {
    shape: [128, 128, 3],
    sum: () => ({
      div: () => ({
        bufferSync: () => ({ values: means }),
      }),
    }),
  };
  return {
    squeeze: () => squeezed,
    dispose: () => {},
    shape: [1, 128, 128, 3],
  };
};

class InferenceWorkerClient {
  constructor(app) {
    this.app = app;
    this.ready = false;
    this.dead = false;
    this.backend = "";
    this.consecutiveErrors = 0;
    this.pending = null; // { id, resolve, reject }
    this.nextId = 1;
    try {
      this.worker = new Worker(
        new URL("../workers/inference.worker.js", import.meta.url),
      );
    } catch (err) {
      clog("worker creation failed", err && err.message);
      this.dead = true;
      return;
    }
    this.worker.onmessage = (e) => this.handleMessage(e.data || {});
    this.worker.onerror = (e) => {
      clog("worker error", e && e.message);
      this.failPending(new Error((e && e.message) || "worker error"));
      this.dead = true;
    };
    this.worker.postMessage({ type: "init", app });
    // If init never completes (e.g. the worker's script/chunk load died
    // before our code could even reply -- seen live), disable the client so
    // callers use their main-thread path instead of waiting forever.
    this.initTimer = setTimeout(() => {
      this.initTimer = null;
      if (!this.ready && !this.dead) {
        clog("init timed out (20s) -> disabling worker inference");
        this.dead = true;
      }
    }, 20000);
  }

  clearInitTimer() {
    if (this.initTimer) {
      clearTimeout(this.initTimer);
      this.initTimer = null;
    }
  }

  handleMessage(msg) {
    if (msg.type === "ready") {
      this.clearInitTimer();
      this.ready = true;
      this.backend = msg.backend;
      clog("ready; backend =", msg.backend);
      return;
    }
    if (msg.type === "init-error") {
      this.clearInitTimer();
      clog("init failed:", msg.message);
      this.dead = true;
      this.failPending(new Error(msg.message));
      return;
    }
    if (msg.type === "diag") {
      clog("worker diag:", msg.message);
      return;
    }
    if (!this.pending || msg.id !== this.pending.id) return;
    const { resolve, reject } = this.pending;
    this.pending = null;
    if (msg.type === "result") {
      this.consecutiveErrors = 0;
      resolve({ detections: msg.detections || [], rgbMeans: msg.rgbMeans });
    } else if (msg.type === "detect-error") {
      this.consecutiveErrors++;
      if (this.consecutiveErrors >= 3) {
        clog("3 consecutive detect errors -> disabling worker inference");
        this.dead = true;
      }
      reject(new Error(msg.message));
    }
  }

  failPending(err) {
    if (this.pending) {
      const { reject } = this.pending;
      this.pending = null;
      reject(err);
    }
  }

  isAlive() {
    return !this.dead;
  }

  async detect(videoEl) {
    if (this.dead) throw new Error("inference worker disabled");
    if (!this.ready) throw new Error("inference worker not ready");
    if (this.pending) throw new Error("detect already in flight");
    // Downscale to the model's input size AT CAPTURE (GPU-accelerated, no
    // readback): the model only ever sees a 128x128 bilinear resize (same as
    // processInput's resizeBilinear), so shipping the full 1080p frame to the
    // worker just to shrink it there doubled the per-frame GPU work and the
    // detection latency (seen live). 128x128 = ~64KB transferred vs ~8MB.
    let bitmap;
    try {
      bitmap = await createImageBitmap(videoEl, {
        resizeWidth: MODEL_INPUT_SIZE,
        resizeHeight: MODEL_INPUT_SIZE,
        resizeQuality: "medium",
      });
    } catch (e) {
      // Old browsers without resize options: send full-res, worker resizes.
      bitmap = await createImageBitmap(videoEl);
    }
    return new Promise((resolve, reject) => {
      const id = this.nextId++;
      this.pending = { id, resolve, reject };
      try {
        this.worker.postMessage({ type: "detect", id, bitmap }, [bitmap]);
      } catch (err) {
        this.pending = null;
        try {
          bitmap.close();
        } catch (e) {}
        reject(err);
      }
    });
  }
}

// Page-lifetime singletons, one per model type ("car" / "bike").
const sharedClients = {};

export const getSharedInferenceWorker = (app) => {
  const key = app || "car";
  if (!isInferenceWorkerSupported()) return null;
  if (!sharedClients[key]) {
    clog("creating shared inference worker for", key);
    sharedClients[key] = new InferenceWorkerClient(key);
  }
  return sharedClients[key];
};
